import { type FieldValidator } from "final-form";

import { REGEX_EMAIL } from "~utils/regex";

export const validateRequired =
  (label: string): FieldValidator<string | undefined> =>
  (value, _, meta) => {
    if (meta?.initial === undefined && !meta?.modified) {
      return;
    }

    if (!value) {
      return `${label} required`;
    }
  };

export const validateEmail: FieldValidator<string | undefined> = (value, _, meta) => {
  if (meta?.initial === undefined && !meta?.modified) {
    return;
  }

  if (!value) {
    return "Email required";
  }

  if (value.length < 5) {
    return 'Email must contain at least 5 characters, e.g. "a@b.c"';
  }

  if (!value.match(REGEX_EMAIL)) {
    return 'Email must be of format "a@b.c"';
  }
};

export const validatePhone: FieldValidator<string | undefined> = (value, _, meta) => {
  if (meta?.initial === undefined && !meta?.modified) {
    return;
  }

  if (!value) {
    return "Phone number required";
  }

  const digitsMatch = value.match(/\d/g);
  if (value.length > 0 && !digitsMatch) {
    return "Phone number must include at least 10 digits, e.g. 5558675309";
  }

  if (digitsMatch) {
    if (value.startsWith("+") && !value.match(/\s/)) {
      return "Phone number requires a space after the country code, e.g. +0049 30128675309";
    }

    const digits = digitsMatch.join("");
    if (digits.length < 10) {
      return "Phone number must include at least 10 digits, e.g. 5558675309";
    }

    if (digits.length > 17) {
      return "Phone number must be no more than 17 digits, e.g. +0049 (30) 128675309";
    }
  }
};

const tUsOnlyPhone = "We only support US phone numbers for texting right now.";

/**
 * Optional phone for account profile forms. Blank is allowed.
 * When a number is present, the selected country (phoneCountryCode) must be US
 * and the national number must be exactly 10 digits.
 */
export const validateOptionalUsPhone: FieldValidator<string | undefined> = (value, allValues, meta) => {
  if (meta?.initial === undefined && !meta?.modified) {
    return;
  }

  const trimmed = value?.trim() ?? "";
  if (!trimmed) {
    return;
  }

  const countryIso =
    typeof allValues === "object" && allValues != null && "phoneCountryCode" in allValues
      ? String((allValues as { phoneCountryCode?: string }).phoneCountryCode ?? "US")
      : "US";

  if (countryIso !== "US") {
    return tUsOnlyPhone;
  }

  const digits = trimmed.match(/\d/g)?.join("") ?? "";
  if (digits.length !== 10) {
    return "Phone number must include exactly 10 digits, e.g. 5558675309";
  }
};

/** Single login field: valid email (regex) or same rules as {@link validatePhone}. */
export const validateEmailOrPhone: FieldValidator<string | undefined> = (value, _, meta) => {
  if (meta?.initial === undefined && !meta?.modified) {
    return;
  }

  if (!value?.trim()) {
    return "Email or phone number required";
  }

  const trimmed = value.trim();

  if (trimmed.includes("@")) {
    if (trimmed.length < 5) {
      return 'Email must contain at least 5 characters, e.g. "a@b.c"';
    }
    if (!trimmed.match(REGEX_EMAIL)) {
      return 'Email must be of format "a@b.c"';
    }
    return;
  }

  return validatePhone(trimmed, _, meta);
};

export const validateZip: FieldValidator<number | undefined> = (value, _, meta) => {
  if (meta?.initial === undefined && !meta?.modified) {
    return;
  }

  if (!value && value !== 0) {
    return "Zip Code required";
  }

  if (String(value).length !== 5) {
    return "Zip Code must be exactly 5 digits, e.g. 32605";
  }
};

export const currentYear = new Date().getFullYear();
export const minGraduationYear = currentYear - 100;
export const maxGraduationYear = currentYear + 12;

export const validateGraduationYear: FieldValidator<number | undefined> = (value, _, meta) => {
  if (meta?.initial === undefined && !meta?.modified) {
    return;
  }

  if (!value && value !== 0) {
    return "Graduation Year required";
  }

  if (String(value).length !== 4) {
    return `Graduation Year must be exactly 4 digits, e.g. ${currentYear}`;
  }

  if (value < minGraduationYear || value > maxGraduationYear) {
    return `Graduation Year must be between ${minGraduationYear} and ${maxGraduationYear}`;
  }
};

export const validateGpaWeighted: FieldValidator<number | undefined> = (value, _, meta) => {
  if (meta?.initial === undefined && !meta?.modified) {
    return;
  }

  if (!value && value !== 0) {
    return;
  }

  if (value < 0) {
    return "GPA must be positive";
  }
};

export const validateGpaUnweighted: FieldValidator<number | undefined> = (value, _, meta) => {
  if (meta?.initial === undefined && !meta?.modified) {
    return;
  }

  if (!value && value !== 0) {
    return;
  }

  if (value < 0) {
    return "GPA must be positive";
  }
};
